import { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { MagnifyingGlass, ClockCounterClockwise, Megaphone, CheckCircle, XCircle, Pulse } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { PromoPageHeader } from "../components/PromoPageHeader";
import { KpiCard } from "../components/KpiCard";
import { EmptyState } from "../components/EmptyState";
import { ListSkeleton } from "../components/Skeleton";
import { PromoSelect } from "../components/PromoSelect";
import { StatusBadge } from "../components/StatusBadge";
import { EventDetailPanel } from "../components/timeline/EventDetailPanel";
import { useTimelineData } from "../usePromo";
import { MARKETPLACES, PLATFORM_BY_KEY } from "../platforms";
import { metaFor, CAT_DOT, CAT_TEXT, CAT_BADGE } from "../eventMeta";
import { fmtDateTime, fmtRelative } from "../campaignUtils";

const mktLabel = (k) => MARKETPLACES.find((m) => m.key === k)?.label || k;

export default function Timeline() {
  const { t } = useTranslation();
  const { events, promoById, promoByCampaign, itemByPostId, loading } = useTimelineData();
  const [q, setQ] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [type, setType] = useState("all");
  const [marketplace, setMarketplace] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [campaign, setCampaign] = useState("all");
  const [sort, setSort] = useState("newest");
  const [selected, setSelected] = useState(null);

  const enriched = useMemo(() => events.map((e) => {
    const p = e.payload || {};
    const postInfo = p.post_id ? itemByPostId[p.post_id] : null;
    let promo = null;
    if (p.promotion_id) promo = promoById[p.promotion_id];
    else if (p.target && promoById[p.target]) promo = promoById[p.target];
    else if (p.campaign_id) promo = promoByCampaign[p.campaign_id];
    else if (postInfo) promo = postInfo.promo;
    let status = p.status || null;
    if (String(e.type).includes("failed")) status = "failed";
    if (!status && typeof p.ok !== "undefined") status = (p.ok === true || p.ok === 1) ? "published" : "failed";
    return {
      ...e,
      _campaign: promo?.name || null, _campaignId: promo?.id || null,
      _product: postInfo?.product || null,
      _platform: p.platform || postInfo?.platform || null,
      _marketplace: promo?.source_platform || null,
      _status: status,
    };
  }), [events, promoById, promoByCampaign, itemByPostId]);

  const counts = useMemo(() => {
    const campaigns = new Set(enriched.map((e) => e._campaignId).filter(Boolean));
    const publications = enriched.filter((e) => metaFor(e.type).cat === "published" && e._status === "published").length;
    const errors = enriched.filter((e) => e._status === "failed").length;
    const last = enriched.map((e) => e.created_at).sort().slice(-1)[0];
    return { total: enriched.length, campaigns: campaigns.size, publications, errors, last };
  }, [enriched]);

  const types = useMemo(() => [...new Set(enriched.map((e) => e.type))].sort(), [enriched]);
  const marketplaces = useMemo(() => [...new Set(enriched.map((e) => e._marketplace).filter(Boolean))], [enriched]);
  const platforms = useMemo(() => [...new Set(enriched.map((e) => e._platform).filter(Boolean))], [enriched]);
  const campaigns = useMemo(() => {
    const seen = {}; enriched.forEach((e) => { if (e._campaignId) seen[e._campaignId] = e._campaign; });
    return Object.entries(seen).map(([id, name]) => ({ id, name }));
  }, [enriched]);

  const inRange = useCallback((iso) => {
    if (dateRange === "all") return true;
    const days = { today: 1, "7d": 7, "30d": 30 }[dateRange];
    return Date.now() - new Date(iso).getTime() <= days * 86400e3;
  }, [dateRange]);

  const filtered = useMemo(() => {
    let list = enriched.filter((e) => {
      if (type !== "all" && e.type !== type) return false;
      if (marketplace !== "all" && e._marketplace !== marketplace) return false;
      if (platform !== "all" && e._platform !== platform) return false;
      if (campaign !== "all" && e._campaignId !== campaign) return false;
      if (!inRange(e.created_at)) return false;
      if (q) {
        const hay = `${e.type} ${e._campaign || ""} ${e._product || ""}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
    return [...list].sort((a, b) => sort === "newest"
      ? new Date(b.created_at) - new Date(a.created_at)
      : new Date(a.created_at) - new Date(b.created_at));
  }, [enriched, type, marketplace, platform, campaign, q, sort, inRange]);

  const opt = (vals, allLabel) => [{ value: "all", label: allLabel }, ...vals];

  return (
    <div className="space-y-6" data-testid="timeline-page">
      <PromoPageHeader overline={t("promo.nav.timeline")} title={t("promo.timeline.title")} description={t("promo.timeline.subtitle")} />

      {/* Overview */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="timeline-overview">
        <KpiCard index={0} testId="tl-total" label={t("promo.timeline.kpi.total")} value={counts.total} Icon={Pulse} />
        <KpiCard index={1} testId="tl-campaigns" label={t("promo.timeline.kpi.campaigns")} value={counts.campaigns} Icon={Megaphone} />
        <KpiCard index={2} testId="tl-publications" label={t("promo.timeline.kpi.publications")} value={counts.publications} Icon={CheckCircle} />
        <KpiCard index={3} testId="tl-errors" label={t("promo.timeline.kpi.errors")} value={counts.errors} Icon={XCircle} />
        <KpiCard index={4} testId="tl-last" label={t("promo.timeline.kpi.lastActivity")} value={counts.last ? fmtRelative(counts.last) : "—"} Icon={ClockCounterClockwise} hint={counts.last ? fmtDateTime(counts.last) : undefined} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("promo.timeline.search")} className="bg-card pl-9" data-testid="timeline-search" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-32"><PromoSelect value={dateRange} onChange={setDateRange} testId="timeline-filter-date"
            options={[{ value: "all", label: t("promo.timeline.date.all") }, { value: "today", label: t("promo.timeline.date.today") }, { value: "7d", label: t("promo.timeline.date.d7") }, { value: "30d", label: t("promo.timeline.date.d30") }]} /></div>
          <div className="w-48"><PromoSelect value={type} onChange={setType} testId="timeline-filter-type"
            options={opt(types.map((tp) => ({ value: tp, label: tp })), t("promo.timeline.allTypes"))} /></div>
          <div className="w-40"><PromoSelect value={marketplace} onChange={setMarketplace} testId="timeline-filter-marketplace"
            options={opt(marketplaces.map((mk) => ({ value: mk, label: mktLabel(mk) })), t("promo.catalog.allMarketplaces"))} /></div>
          <div className="w-36"><PromoSelect value={platform} onChange={setPlatform} testId="timeline-filter-platform"
            options={opt(platforms.map((p) => ({ value: p, label: PLATFORM_BY_KEY[p]?.label || p })), t("promo.queue.allPlatforms"))} /></div>
          <div className="w-44"><PromoSelect value={campaign} onChange={setCampaign} testId="timeline-filter-campaign"
            options={opt(campaigns.map((c) => ({ value: c.id, label: c.name })), t("promo.queue.allCampaigns"))} /></div>
          <div className="w-36"><PromoSelect value={sort} onChange={setSort} testId="timeline-sort"
            options={[{ value: "newest", label: t("promo.timeline.sort.newest") }, { value: "oldest", label: t("promo.timeline.sort.oldest") }]} /></div>
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <ListSkeleton rows={7} />
      ) : enriched.length === 0 ? (
        <EmptyState Icon={ClockCounterClockwise} title={t("promo.timeline.emptyTitle")} description={t("promo.timeline.emptyDesc")} />
      ) : filtered.length === 0 ? (
        <EmptyState Icon={MagnifyingGlass} title={t("promo.catalog.noMatch")} description={t("promo.catalog.noMatchDesc")} />
      ) : (
        <div className="relative space-y-3 border-l border-border pl-8" data-testid="timeline-list">
          {filtered.map((e, i) => {
            const { Icon, cat } = metaFor(e.type);
            const mkt = e._marketplace ? MARKETPLACES.find((m) => m.key === e._marketplace) : null;
            const plat = e._platform ? PLATFORM_BY_KEY[e._platform] : null;
            return (
              <motion.button
                key={e.id} onClick={() => setSelected(e)}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: Math.min(i * 0.015, 0.3) }}
                className="group relative block w-full rounded-lg border border-border bg-card p-4 text-left transition-colors hover:bg-accent/30"
                data-testid={`timeline-event-${e.id}`}
              >
                <span className={`absolute -left-[38px] top-4 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-card`}>
                  <Icon size={13} className={CAT_TEXT[cat]} />
                </span>
                <span className={`absolute -left-[33px] top-11 bottom-0 w-px ${CAT_DOT[cat]} opacity-20`} />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={CAT_BADGE[cat]} label={t(`promo.timeline.cat.${cat}`, cat)} dot={false} />
                    <span className="font-mono text-xs text-muted-foreground">{e.type}</span>
                  </div>
                  <span className="text-xs tabular-nums text-muted-foreground" title={fmtDateTime(e.created_at)}>{fmtRelative(e.created_at)}</span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {e._campaign && <span className="flex items-center gap-1"><Megaphone size={11} />{e._campaign}</span>}
                  {e._product && <span className="font-mono">{e._product}</span>}
                  {mkt && <span className="flex items-center gap-1"><mkt.Icon size={11} />{mkt.label}</span>}
                  {plat && <span className="flex items-center gap-1"><plat.Icon size={11} />{plat.label}</span>}
                  {e._status && <StatusBadge status={e._status} label={t(`promo.queue.status.${e._status}`, e._status)} />}
                </div>
              </motion.button>
            );
          })}
        </div>
      )}

      <EventDetailPanel event={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
